import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { Role } from "@/utils/hooks/use-role";
import { makeShiftsQuery, makeWorkerPreviousShiftsQuery } from "..";
import { useQuery } from "@tanstack/react-query";
import { useRole } from "@/utils/hooks/use-role";
import { BusinessProfileCard } from "@/components/BusinessProfileCard";
import { WorkerProfileCard } from "@/components/WorkerProfileCard";
import { makeProfileQuery as makeBusinessProfileQuery } from "@/routes/app/profile/employer/$employerId/-queries";
import { makeProfileQuery as makeWorkerProfileQuery } from "@/routes/app/profile/worker/$workerId/-queries";
import MainGrid from "@/components/main-grid";
import BackButton from "@/components/back-button";
import { Line } from "@/components/shift-card";
import { RoleIcon } from "@/components/icons/role-icons";
import i18n from "@/i18n";
import { localeFor } from "@/utils/formatters";
import { ROLE_OPTIONS as roles } from "@/constants/roles";
import { getShiftDetail } from "@/queries/api/employerDashboardApi";
import type { ShiftDetail } from "@/queries/api/employerDashboardApi";
import { QueryKeys } from "@/queries/queryKeys";

const ROLES = ["worker", "employer"] as const;

function makeEmployerShiftDetailQuery(shiftId: number) {
  return {
    queryKey: [QueryKeys.UPCOMING_SHIFTS, "detail", shiftId],
    queryFn: () => getShiftDetail(shiftId),
  };
}

type DetailCard = {
  name: string;
  roleLabel: string;
  roleValue: string;
  ratePerHour: number;
  total?: number;
  dateLabel: string;
  timeLabel: string;
  venue: string;
  businessName?: string;
  workerId?: string;
  workerName?: string;
  description?: string;
};

function formatDate(dateStr?: string | null) {
  if (!dateStr) return "";
  try {
    const d = new Date(`${dateStr}T00:00:00`);
    return d.toLocaleDateString(localeFor(i18n.language || "en"), {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function formatTime(t?: string | null) {
  if (!t) return "";
  const [hour, minute] = t.split(":");
  const d = new Date();
  d.setHours(Number(hour));
  d.setMinutes(Number(minute));
  return d.toLocaleTimeString(localeFor(i18n.language || "en"), {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function shiftDetailToCard(detail: ShiftDetail): DetailCard {
  const roleName = detail.role_name ?? "";
  const roleValue = roleName
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
  const roleObj = roles.find((r: { value: string }) => r.value === roleValue);
  const roleLabel = roleObj ? roleObj.label : roleName;

  const wageNum =
    typeof detail.wage === "number"
      ? detail.wage
      : detail.wage != null
        ? Number(detail.wage)
        : 0;

  let total: number | undefined;
  if (
    detail.start_date &&
    detail.start_time &&
    detail.end_time &&
    Number.isFinite(wageNum)
  ) {
    const start = new Date(`${detail.start_date}T${detail.start_time}`);
    const end = new Date(`${detail.start_date}T${detail.end_time}`);
    const hours = (end.getTime() - start.getTime()) / 3_600_000;
    if (hours > 0) total = hours * wageNum;
  }

  const startLabel = formatDate(detail.start_date);
  const endLabel = formatDate(detail.end_date);
  const dateLabel =
    endLabel && endLabel !== startLabel
      ? `${startLabel} – ${endLabel}`
      : startLabel;

  const timeLabel =
    detail.start_time && detail.end_time
      ? `${formatTime(detail.start_time)} – ${formatTime(detail.end_time)}`
      : "";

  return {
    name: detail.name ?? "",
    roleLabel,
    roleValue,
    ratePerHour: wageNum,
    total,
    dateLabel,
    timeLabel,
    venue: detail.location ?? "",
    businessName: detail.business?.name ?? undefined,
    workerId: detail.assigned_worker?.user_id,
    workerName: detail.assigned_worker?.full_name ?? undefined,
    description: detail.role_description ?? undefined,
  };
}

export const Route = createFileRoute(
  "/app/$role/_app/dashboard/shift/$shiftId",
)({
  component: ShiftDetailsPage,
  beforeLoad: ({ params }) => {
    if (!ROLES.includes(params.role as Role)) {
      throw new Error(`Invalid role: ${params.role}`);
    }
  },
  loader: ({ params, context }) => {
    if (params.role === "employer") {
      const shiftId = Number(params.shiftId);
      if (Number.isFinite(shiftId)) {
        context.queryClient.ensureQueryData(
          makeEmployerShiftDetailQuery(shiftId),
        );
      }
    } else if (params.role === "worker") {
      context.queryClient.ensureQueryData(makeShiftsQuery("worker"));
      context.queryClient.ensureQueryData(makeWorkerPreviousShiftsQuery());
    }
  },
  staticData: {
    layout: {
      boxed: true,
    },
  },
});

export default function ShiftDetailsPage() {
  const { t } = useTranslation("dashboard");
  const { shiftId } = Route.useParams();
  const role = useRole();
  const isWorker = role === "worker";
  const isEmployer = role === "employer";

  const shiftIdNum = Number(shiftId);

  const { data: workerShifts, isLoading: isWorkerShiftsLoading } = useQuery({
    ...makeShiftsQuery(role),
    enabled: isWorker,
  });
  const {
    data: workerPreviousShifts,
    isLoading: isWorkerPreviousShiftsLoading,
  } = useQuery({
    ...makeWorkerPreviousShiftsQuery(),
    enabled: isWorker,
  });
  const workerShift = isWorker
    ? (workerShifts?.find((s) => s.id === shiftId.toString()) ??
      workerPreviousShifts?.find((s) => s.id === shiftId.toString()))
    : undefined;

  const { data: employerDetail, isLoading: isEmployerDetailLoading } = useQuery(
    {
      ...makeEmployerShiftDetailQuery(shiftIdNum),
      enabled: isEmployer && Number.isFinite(shiftIdNum),
    },
  );
  const employerCard = employerDetail
    ? shiftDetailToCard(employerDetail)
    : undefined;

  const businessId =
    isWorker && workerShift?.businessId
      ? String(workerShift.businessId)
      : undefined;
  const workerId = isEmployer ? employerCard?.workerId : undefined;

  const { data: businessProfile, isLoading: isBusinessLoading } = useQuery({
    ...makeBusinessProfileQuery(businessId!),
    enabled: Boolean(isWorker && businessId),
  });
  const { data: workerProfile, isLoading: isWorkerLoading } = useQuery({
    ...makeWorkerProfileQuery(workerId),
    enabled: Boolean(isEmployer && workerId),
  });

  if (
    (isWorker && (isWorkerShiftsLoading || isWorkerPreviousShiftsLoading)) ||
    (isEmployer && isEmployerDetailLoading) ||
    (isWorker && businessId && isBusinessLoading) ||
    (isEmployer && workerId && isWorkerLoading)
  ) {
    return <div className="p-8">{t("shiftDetails.loading")}</div>;
  }

  const shift: DetailCard | undefined = isWorker
    ? workerShift
      ? {
          name: workerShift.name,
          roleLabel: workerShift.roleLabel,
          roleValue: workerShift.roleValue,
          ratePerHour: workerShift.ratePerHour,
          total: workerShift.total,
          dateLabel: workerShift.dateLabel,
          timeLabel: workerShift.timeLabel,
          venue: workerShift.venue,
          businessName: workerShift.businessName,
          workerId: workerShift.workerId,
          workerName: workerShift.workerName,
          description: workerShift.description,
        }
      : undefined
    : employerCard;

  if (!shift) {
    return <div className="p-8">{t("shiftDetails.notFound")}</div>;
  }

  return (
    <div className="flex flex-col w-full gap-3 bg-fog">
      <div className="flex items-start">
        <BackButton />
      </div>
      <MainGrid>
        <div className="col-span-1">
          {isWorker && businessProfile && (
            <BusinessProfileCard business={businessProfile.business} />
          )}
          {isEmployer && workerProfile && (
            <WorkerProfileCard profile={workerProfile.profile} />
          )}
        </div>
        <div className="col-span-2 w-full rounded-t-3xl bg-white p-6">
          <div className="flex items-center gap-4 mb-6">
            <h2 className="heading-2 flex-1">{t("shiftDetails.title")}</h2>
            <span className="shrink-0">
              {shift.roleValue && (
                <RoleIcon
                  value={shift.roleValue}
                  width={56}
                  height={56}
                  variant="gradient"
                />
              )}
            </span>
          </div>
          <div className="space-y-2 text-garnet">
            <div
              className="text-[20px] font-bold leading-tight mb-1 truncate"
              title={shift.name}
            >
              {shift.name}
            </div>
            <div className="text-[16px] font-semibold">
              {shift.roleLabel}
              <span className="font-normal text-garnet/70">
                {t("shiftDetails.ratePerHour", { rate: shift.ratePerHour })}
                {shift.total !== undefined
                  ? ` (${t("shiftDetails.total", { amount: shift.total.toFixed(2) })})`
                  : ""}
              </span>
            </div>
            <div className="space-y-1.5">
              <Line icon="cal">{shift.dateLabel}</Line>
              <Line icon="clock">{shift.timeLabel}</Line>
              <Line icon="pin">{shift.venue}</Line>
              {isWorker ? (
                <Line icon="user">{shift.businessName}</Line>
              ) : shift.workerName ? (
                <Line icon="user">{shift.workerName}</Line>
              ) : null}
            </div>
            {shift.description && (
              <div className="mt-4 text-[15px] text-garnet/90">
                {shift.description}
              </div>
            )}
          </div>
        </div>
      </MainGrid>
    </div>
  );
}
